<template>
  <!-- S10 / IC4 -- the PREF-05 permission explainer.
       ==========================================================================
       WHY THIS SHEET EXISTS AT ALL

       iOS's notification permission prompt is ONE-SHOT. A member who declines a
       cold system dialog can never be asked again from inside the app; they are
       left with a Settings-app-only path forever. This sheet is what protects
       that single shot: it asks in Dutch, in context, right after a booking, and
       the system dialog is only reached by a member who has already said yes
       here.

       The PARENT owns `show` (calendar.vue). This component never writes it --
       it reports that the sheet has gone away by emitting `closed`, which is the
       one event every dismissal route ends in: the primary button, `Niet nu`,
       the toolbar link, a backdrop tap and a swipe-down.
       ====================================================================== -->
  <f7-sheet
      class="notification-permission-sheet"
      :backdrop="true"
      :opened="show"
      @sheet:closed="on_sheet_closed"
  >
    <f7-toolbar>
      <div class="left">

      </div>
      <div class="right">
        <f7-link sheet-close>
          Sluiten
        </f7-link>
      </div>
    </f7-toolbar>

    <f7-block-header class="notification-permission-title">
      {{ title }}
    </f7-block-header>

    <f7-block class="notification-permission-body">
      {{ body }}
    </f7-block>

    <f7-block-footer>
      <!-- The one accent-filled element in this sheet, and the ONLY control that
           can ever reach the OS prompt. `sheet-close` is on it deliberately: the
           sheet has to be gone before the system dialog is asked for, and
           carrying the close attribute means the closing does not depend on the
           parent having wired `closed` up. What it does NOT do is dispatch --
           see accept(). -->
      <f7-button class="mt-3" fill large sheet-close @click="accept">
        Notificaties aanzetten
      </f7-button>

      <!-- DECLINING IS A DEAD END ON PURPOSE, AND THAT IS THE WHOLE DESIGN.

           `Niet nu` closes the sheet and does nothing else. No handler, no
           dispatch, no `refresh_permission`, no flag written here. The OS prompt
           is not called, so iOS's one shot is NOT burned and the member can
           still be asked later -- the notifications settings screen keeps
           offering it through the S4 `prompt` banner (PREF-04).

           A future contributor "tidying this up" by giving this link a handler
           that asks the OS anyway would spend that one shot on a member who just
           said no, permanently, on every device that ships it. There is a test
           named `declining never reaches the OS prompt` that fails the moment
           this stops being an inert link. -->
      <div class="mt-3 text-align-center">
        <f7-link sheet-close>
          Niet nu
        </f7-link>
      </div>

      <div class="mt-3 notification-permission-footnote">
        Je kunt dit later altijd aanzetten via Account > Notificaties in het menu.
      </div>
    </f7-block-footer>

  </f7-sheet>
</template>

<script>

/**
 * The permission explainer that precedes the OS prompt (PREF-05, S10, IC4).
 *
 * Everything rendered above is fixed authored Dutch, taken verbatim from the
 * phase's copywriting contract. NOTHING is interpolated -- not a member name,
 * not a slot time, not a server string -- so this sheet has no injection surface
 * and no long-text case that is not known at build time (T-39-54).
 *
 * It has no loading state and no error state either. There is no async content
 * to wait for, and a member who declines the OS prompt has not failed at
 * anything worth reporting back at them: the outcome is carried by the banner on
 * the notifications settings screen, which by then reads `denied` instead of
 * `prompt`.
 */
export default {
  components: {},

  props: {
    // Parent-owned, exactly like FeedbackSheetModal. The parent flips it to
    // false in its `closed` handler.
    'show': {default: false},
    // Authored Dutch only, never server strings or member data (T-39-54). The
    // defaults are the after-first-booking copy; app.vue passes the login copy.
    'title': {default: 'Zullen we je aan je reservering herinneren?'},
    'body': {default: 'Met notificaties krijg je een herinnering voor je training en hoor je het meteen als er iets met je reservering verandert. Je kiest zelf waarover je bericht krijgt.'},
  },

  mixins: [
    //
  ],

  data: function () {
    return {
      // Set by accept(), consumed by on_sheet_closed(). This flag is the ONLY
      // thing that distinguishes an accept from a dismissal, and therefore the
      // only thing that can lead to the OS being asked.
      accept_pending: false,
    };
  },

  computed: {
    //
  },

  created() {
    //
  },

  mounted() {
    //
  },

  methods: {
    /**
     * The member said yes. Close first; ask the OS afterwards.
     *
     * This is the one point where this component diverges from
     * FeedbackSheetModal, which emits `closed` AFTER it acts. Here the order is
     * reversed and it matters: the OS permission dialog is a native layer drawn
     * over the whole WebView, and asking for it while this sheet is still on
     * screen stacks a system alert on top of an explainer that is still asking
     * the same question. So nothing is dispatched here. The request waits for
     * the sheet's own `sheet:closed`, which Framework7 fires when the closing
     * animation has actually finished -- not when it starts.
     */
    accept() {
      this.accept_pending = true;

      this.$emit('closed');
    },

    /**
     * Every dismissal route lands here -- accept, `Niet nu`, the toolbar link, a
     * backdrop tap and a swipe-down alike.
     *
     * That is what makes "backdrop tap and swipe-down behave exactly like Niet
     * nu" true by construction rather than by a second code path that has to be
     * kept in step: the accept flag is set in accept() and nowhere else, so
     * every route that did not go through accept() falls out at the guard below
     * having done nothing but close.
     *
     * DO NOT move the dispatch out of that guard. An unconditional dispatch here
     * would make a backdrop tap ask iOS for permission -- spending the one-shot
     * prompt on a member who dismissed the explainer without reading it.
     */
    on_sheet_closed() {
      this.$emit('closed');

      if (!this.accept_pending) {
        return;
      }

      this.accept_pending = false;

      this.$store.dispatch('push/request_permission_and_register')
        .catch(() => {
          // Deliberately silent. The plugin throwing, or the member declining
          // the system dialog, both end here, and neither is something to show a
          // member who has just successfully booked a training. The settings
          // screen's banner is where the resulting state is surfaced.
        });
    },
  },

  watch: {
    //
  }
};
</script>

<style>
/* --------------------------------------------------------------------------
 * Height.
 *
 * Framework7's `.sheet-modal` is a FIXED `height: var(--f7-sheet-height)`, which
 * ships as 260px, and `.sheet-modal { overflow: hidden }` means anything past
 * that is not scrollable, it is invisible. This sheet's copy -- a two-line
 * question, a three-to-five-line body, a button, a link and a footnote -- runs
 * past 260px on a narrow screen, so the footnote would simply not be there.
 *
 * Set through the variable rather than as `height: auto`, because the project's
 * own src/css/components/sheet-modal.scss already overrides `height` by class
 * (`.high`, `.highest`) and a same-specificity override would then depend on
 * stylesheet injection order. Overriding the variable the library's own rule
 * reads is order-independent.
 *
 * `.high` (75%) is not used here on purpose: it is the right height for
 * FeedbackSheetModal's text editor and the wrong one for five short lines, which
 * would sit in the top quarter of an otherwise empty sheet.
 * ------------------------------------------------------------------------ */
.notification-permission-sheet {
  --f7-sheet-height: auto;
}

/* Heading role, 16px/600 -- the same tokens the settings screen's banner heading
   and group titles use, so this reads as a heading and not as the 14px
   f7-block-header default it would otherwise inherit. */
.notification-permission-sheet .notification-permission-title {
  font-size: var(--f7-block-title-font-size);
  font-weight: var(--f7-block-title-font-weight);
  line-height: var(--f7-block-title-line-height);
  color: var(--f7-text-color);
}

/* Body role, 15px/400 -- --f7-list-item-text-font-size, the same token the
   permission banner's body uses on the settings screen. Subordinate to the
   16px heading above it, louder than the 14px footnote below. */
.notification-permission-sheet .notification-permission-body {
  font-size: var(--f7-list-item-text-font-size);
}

/* Footnote role. `f7-block-footer` is already 14px in the secondary text
   colour; the nested div would otherwise inherit the sheet's base size, so it
   is pinned to the footnote token explicitly. Not <small>, which
   src/css/components/general.scss sets to 0.65em -- about 9px here. */
.notification-permission-sheet .notification-permission-footnote {
  font-size: var(--f7-block-footer-font-size);
}
</style>
